import { useEffect, useRef, useState } from 'react'
import { Check, Settings } from 'lucide-react'
import { cn } from '@/lib/utils'
import { resolveSource, type QualitySelection, type VideoSource } from './quality'

/**
 * Player de video da aplicacao. Ponto unico de mudanca dos controles: quem
 * precisar de um `<video>` com controles deve usar este componente, e nao um
 * `<video controls>` solto — hoje ele atende o aluno (resultado da prova) e o
 * preview do admin.
 *
 * Usa os controles nativos do navegador (play, volume, velocidade, tela cheia) e
 * acrescenta so o que eles nao tem: um menu de configuracoes com a escolha de
 * qualidade. O download fica desabilitado — `controlsList="nodownload"` tira o
 * item do menu nativo e o `onContextMenu` tira o "Salvar video como..." — e nao
 * ha faixa de legenda, entao o botao de legenda nao aparece.
 */

interface Props {
  sources: VideoSource[]
  posterUrl?: string | null
  /** Identidade da midia: ao trocar, o `<video>` e remontado do zero. */
  mediaKey: string | number
}

export default function VideoPlayer({ sources, posterUrl = null, mediaKey }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const resumeAtRef = useRef(0)
  const wasPlayingRef = useRef(true)

  const [selection, setSelection] = useState<QualitySelection>('auto')
  const [menuOpen, setMenuOpen] = useState(false)
  // Largura medida antes do primeiro render do <video> — resolver a qualidade
  // depois que ele carregou obrigaria a recarregar a fonte. Medida uma unica
  // vez de proposito: redimensionar a janela nao deve reiniciar o download.
  const [renderedWidth, setRenderedWidth] = useState<number | null>(null)

  const attachContainer = (node: HTMLDivElement | null) => {
    containerRef.current = node

    if (node && renderedWidth === null) setRenderedWidth(node.clientWidth)
  }

  useEffect(() => {
    if (!menuOpen) return

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('mousedown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)

    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [menuOpen])

  // Fonte sem `url` nao toca — filtrar aqui evita um <video> mudo, sem src, que
  // parece um player quebrado sem dizer o motivo.
  const playable = sources.filter((source) => Boolean(source.url))

  const currentSource =
    renderedWidth === null ? null : resolveSource(playable, selection, renderedWidth)

  /** Guarda a posicao atual para que a troca de qualidade nao volte ao inicio. */
  const changeSelection = (next: QualitySelection) => {
    const video = containerRef.current?.querySelector('video')

    resumeAtRef.current = video?.currentTime ?? 0
    wasPlayingRef.current = video ? !video.paused : true

    setSelection(next)
    setMenuOpen(false)
  }

  const restorePosition = (video: HTMLVideoElement) => {
    if (resumeAtRef.current > 0) video.currentTime = resumeAtRef.current
    if (wasPlayingRef.current) void video.play().catch(() => undefined)
  }

  const autoLabel = resolveSource(playable, 'auto', renderedWidth ?? 0)?.label

  if (renderedWidth !== null && !currentSource) {
    return (
      <div className="w-full aspect-video rounded-lg bg-black flex items-center justify-center">
        <p className="text-sm text-neutral-400">Vídeo indisponível no momento.</p>
      </div>
    )
  }

  return (
    <div ref={attachContainer} className="relative">
      {currentSource && (
        <>
          {/* `key` na fonte: sem ele o React reaproveita o mesmo elemento ao
              trocar de qualidade e o navegador ignora o novo src. */}
          <video
            key={`${mediaKey}-${currentSource.label}`}
            src={currentSource.url}
            poster={posterUrl ?? undefined}
            controls
            autoPlay
            controlsList="nodownload"
            onContextMenu={(event) => event.preventDefault()}
            onLoadedMetadata={(event) => restorePosition(event.currentTarget)}
            className="w-full rounded-lg bg-black"
          />

          {playable.length > 1 && (
            <div ref={menuRef} className="absolute top-2 right-2">
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                aria-label="Configurações do vídeo"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
              >
                <Settings className="h-4 w-4" />
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 min-w-[10rem] rounded-lg bg-neutral-900/95 text-white shadow-lg ring-1 ring-white/10 py-1"
                >
                  <p className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-neutral-400">
                    Qualidade
                  </p>
                  <QualityOption
                    label="Automática"
                    hint={autoLabel}
                    selected={selection === 'auto'}
                    onSelect={() => changeSelection('auto')}
                  />
                  {playable.map((source) => (
                    <QualityOption
                      key={source.label}
                      label={source.label}
                      selected={selection === source.label}
                      onSelect={() => changeSelection(source.label)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function QualityOption({
  label,
  hint,
  selected,
  onSelect,
}: {
  label: string
  hint?: string
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
        selected ? 'text-primary-400' : 'text-neutral-200 hover:bg-white/10'
      )}
    >
      <Check className={cn('h-3.5 w-3.5 shrink-0', !selected && 'opacity-0')} />
      <span className="flex-1">{label}</span>
      {hint && <span className="text-xs text-neutral-400">{hint}</span>}
    </button>
  )
}
